<template>
  <div class="streamer-public-report">
    <div class="spr-header">
      <h1 class="spr-title">Streamer Report</h1>
      <p v-if="report" class="spr-subtitle mb-0">
        {{ report.streamer.name }}
        <code class="spr-code">{{ report.streamer.referral_code }}</code>
      </p>
      <p v-else-if="error" class="spr-error mb-0">{{ error }}</p>
    </div>

    <div class="spr-filters spr-panel mb-3">
      <div class="spr-panel__body py-3">
        <div class="row align-items-end">
          <div class="col-6 col-md-4 mb-2 mb-md-0">
            <label class="spr-label">From</label>
            <input v-model="startDate" type="date" class="form-control form-control-sm spr-input" />
          </div>
          <div class="col-6 col-md-4 mb-2 mb-md-0">
            <label class="spr-label">To</label>
            <input v-model="endDate" type="date" class="form-control form-control-sm spr-input" />
          </div>
          <div class="col-12 col-md-4">
            <button type="button" class="btn btn-sm btn-block spr-refresh-btn" :disabled="loading" @click="fetchReport">
              <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loading }"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading && !report" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
    </div>

    <template v-if="report">
      <div class="row">
        <div class="col-6 col-md-4 mb-3">
          <div class="spr-stat-card">
            <p class="spr-stat-label">Total Users</p>
            <h3 class="spr-stat-value spr-stat-value--default">{{ formatCount(report.total_users) }}</h3>
          </div>
        </div>
        <div class="col-6 col-md-4 mb-3">
          <div class="spr-stat-card">
            <p class="spr-stat-label">New Users</p>
            <h3 class="spr-stat-value spr-stat-value--success">+{{ formatCount(report.new_users) }}</h3>
          </div>
        </div>
        <div class="col-6 col-md-4 mb-3">
          <div class="spr-stat-card">
            <p class="spr-stat-label">Active Users</p>
            <h3 class="spr-stat-value spr-stat-value--default">{{ formatCount(report.active_users) }}</h3>
          </div>
        </div>
        <div class="col-6 col-md-4 mb-3">
          <div class="spr-stat-card spr-stat-deposit">
            <p class="spr-stat-label">Deposit</p>
            <h3 class="spr-stat-value">{{ formatAmount(report.total_deposit) }}</h3>
          </div>
        </div>
        <div class="col-6 col-md-4 mb-3">
          <div class="spr-stat-card spr-stat-withdraw">
            <p class="spr-stat-label">Withdraw</p>
            <h3 class="spr-stat-value">{{ formatAmount(report.total_withdraw) }}</h3>
          </div>
        </div>
        <div class="col-6 col-md-4 mb-3">
          <div class="spr-stat-card" :class="report.profit >= 0 ? 'spr-stat-profit' : 'spr-stat-loss'">
            <p class="spr-stat-label">Profit</p>
            <h3 class="spr-stat-value">
              {{ report.profit >= 0 ? '+' : '' }}{{ formatAmount(report.profit) }}
            </h3>
          </div>
        </div>
      </div>

      <div class="spr-panel mb-3">
        <div class="spr-panel__header py-3">
          <h5 class="spr-panel__title mb-0">
            Referred Users
            <span v-if="usersPagination.total" class="spr-panel__count">
              ({{ formatCount(usersPagination.total) }})
            </span>
          </h5>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0 spr-table">
            <thead>
              <tr>
                <th class="spr-col-no">#</th>
                <th>User</th>
                <th>Phone</th>
                <th>Joined</th>
                <th class="text-right">Deposit</th>
                <th class="text-right">Withdraw</th>
                <th class="text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(user, index) in users" :key="user.id">
                <td class="spr-col-no">{{ rowNumber(index) }}</td>
                <td>{{ user.user_name }}</td>
                <td>{{ user.phone }}</td>
                <td>{{ formatDate(user.created_at) }}</td>
                <td class="text-right spr-text-success">{{ formatAmount(user.all_deposit) }}</td>
                <td class="text-right spr-text-danger">{{ formatAmount(user.all_withdraw) }}</td>
                <td class="text-right" :class="user.profit >= 0 ? 'spr-text-success' : 'spr-text-danger'">
                  {{ formatAmount(user.profit) }}
                </td>
              </tr>
              <tr v-if="!users.length && !usersLoading">
                <td colspan="7" class="text-center spr-empty py-4">No users found</td>
              </tr>
              <tr v-if="usersLoading">
                <td colspan="7" class="text-center spr-empty py-4">
                  <i class="fas fa-spinner fa-spin mr-1"></i>
                  Loading users...
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="usersPagination.total > 0" class="spr-panel__footer d-flex justify-content-between align-items-center flex-wrap">
          <span class="spr-page-info mb-2 mb-md-0">
            Showing {{ formatCount(usersPagination.from) }}–{{ formatCount(usersPagination.to) }}
            of {{ formatCount(usersPagination.total) }} users
          </span>
          <div v-if="usersPagination.last_page > 1" class="d-flex align-items-center">
          <button
            type="button"
            class="btn btn-sm spr-page-btn"
            :disabled="usersPagination.current_page <= 1"
            @click="changeUsersPage(usersPagination.current_page - 1)"
          >
            Previous
          </button>
          <span class="spr-page-info mx-2">
            Page {{ usersPagination.current_page }} / {{ usersPagination.last_page }}
          </span>
          <button
            type="button"
            class="btn btn-sm spr-page-btn"
            :disabled="usersPagination.current_page >= usersPagination.last_page"
            @click="changeUsersPage(usersPagination.current_page + 1)"
          >
            Next
          </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    const today = moment().format('YYYY-MM-DD');

    return {
      code: '',
      startDate: today,
      endDate: today,
      report: null,
      users: [],
      usersPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0,
      },
      loading: false,
      usersLoading: false,
      error: '',
    };
  },
  created() {
    this.code = this.$route.params.code || this.$route.query.by || '';
    if (!this.code) {
      this.error = 'Missing agent code.';
      return;
    }
    this.fetchReport();
  },
  watch: {
    '$route.params.code'(value) {
      this.code = value || this.$route.query.by || '';
      if (this.code) {
        this.fetchReport();
      }
    },
    '$route.query.by'(value) {
      if (!this.$route.params.code && value) {
        this.code = value;
        this.fetchReport();
      }
    },
  },
  methods: {
    dateRangeParams() {
      return {
        dateRange: JSON.stringify({
          startDate: this.startDate,
          endDate: this.endDate,
        }),
      };
    },
    async fetchReport() {
      if (!this.code) return;

      this.loading = true;
      this.error = '';

      try {
        const response = await axios.get(`/streamers/public/${encodeURIComponent(this.code)}/report`, {
          params: this.dateRangeParams(),
        });

        this.report = response.data.data;
        this.usersPagination.current_page = 1;
        await this.fetchUsers(1);
      } catch (err) {
        this.report = null;
        this.users = [];
        this.error = err.response?.data?.message || 'Unable to load report.';
      } finally {
        this.loading = false;
      }
    },
    async fetchUsers(page = 1) {
      if (!this.code) return;

      this.usersLoading = true;

      try {
        const response = await axios.get(`/streamers/public/${encodeURIComponent(this.code)}/users`, {
          params: {
            page,
            per_page: 20,
            ...this.dateRangeParams(),
          },
        });

        const payload = response.data.data;
        this.users = payload.data || [];
        this.usersPagination = {
          current_page: payload.current_page || 1,
          last_page: payload.last_page || 1,
          per_page: payload.per_page || 20,
          total: payload.total || 0,
          from: payload.from || 0,
          to: payload.to || 0,
        };
      } catch (err) {
        this.users = [];
      } finally {
        this.usersLoading = false;
      }
    },
    changeUsersPage(page) {
      this.fetchUsers(page);
    },
    rowNumber(index) {
      const { current_page, per_page } = this.usersPagination;
      return ((current_page - 1) * per_page) + index + 1;
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString();
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString();
    },
    formatDate(value) {
      return value ? moment(value).format('DD MMM YYYY') : '-';
    },
  },
};
</script>

<style scoped>
/* .streamer-public-report {
  padding: 1rem 0.75rem calc(2.5rem + env(safe-area-inset-bottom, 0px));
  min-height: 100vh;
  background: #0a0a0a;
  color: #f5f5f5;
} */

.streamer-public-report {
  padding: 1rem 0.75rem calc(2.5rem + env(safe-area-inset-bottom, 0px));
  height: 100vh;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #0a0a0a;
  color: #f5f5f5;
}

@media (max-width: 768px) {
  .streamer-public-report {
    padding-bottom: calc(4rem + env(safe-area-inset-bottom, 0px));
  }

  .spr-panel__footer {
    padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
  }
}

.spr-header {
  margin-bottom: 1.25rem;
}

.spr-title {
  font-size: 1.45rem;
  font-weight: 800;
  margin-bottom: 0.4rem;
  color: #f0d78c;
  letter-spacing: 0.02em;
}

.spr-subtitle {
  color: #e8e8e8;
  font-size: 0.95rem;
  font-weight: 500;
}

.spr-code {
  background: rgba(207, 167, 87, 0.15);
  color: #7dffb8;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.35rem;
  font-weight: 600;
}

.spr-error {
  color: #ff8a9b;
  font-weight: 600;
}

.spr-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #d4d4d4;
  margin-bottom: 0.35rem;
}

.spr-panel {
  background: #161616;
  border: 1px solid rgba(207, 167, 87, 0.25);
  border-radius: 12px;
  overflow: hidden;
}

.spr-panel__body {
  padding: 0 1rem;
}

.spr-panel__header {
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #2e2e2e;
  background: #1c1c1c;
}

.spr-panel__title {
  font-size: 1rem;
  font-weight: 800;
  color: #f0d78c;
  letter-spacing: 0.03em;
}

.spr-panel__count {
  color: #e8e8e8;
  font-weight: 600;
}

.spr-col-no {
  width: 2.5rem;
  color: #b8b8b8 !important;
  font-size: 0.82rem;
  font-weight: 600;
}

.spr-panel__footer {
  padding: 0.85rem 1rem;
  border-top: 1px solid #2e2e2e;
  background: #1c1c1c;
}

.spr-input,
.spr-filters .form-control {
  background: #242424;
  border-color: #444;
  color: #fff;
}

.spr-input:focus,
.spr-filters .form-control:focus {
  background: #2a2a2a;
  border-color: #cfa757;
  color: #fff;
  box-shadow: 0 0 0 0.15rem rgba(207, 167, 87, 0.2);
}

.spr-refresh-btn {
  background: linear-gradient(135deg, #cfa757 0%, #f0d78c 100%);
  border: none;
  color: #1a1a1a;
  font-weight: 700;
}

.spr-refresh-btn:disabled {
  opacity: 0.65;
}

.spr-stat-card {
  background: #161616;
  border: 1px solid #333;
  border-radius: 10px;
  padding: 1rem;
  height: 100%;
}

.spr-stat-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c8c8c8;
  margin-bottom: 0.35rem;
}

.spr-stat-value {
  font-size: 1.25rem;
  font-weight: 800;
  margin-bottom: 0;
}

.spr-stat-value--default {
  color: #ffffff;
}

.spr-stat-value--success,
.spr-stat-deposit .spr-stat-value,
.spr-stat-profit .spr-stat-value {
  color: #5dffb0;
}

.spr-stat-withdraw .spr-stat-value,
.spr-stat-loss .spr-stat-value {
  color: #ff7b8f;
}

.spr-table {
  color: #f0f0f0;
}

.spr-table thead th {
  border-top: 0;
  border-color: #333;
  background: #1f1f1f;
  color: #e0e0e0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.spr-table td {
  border-color: #2e2e2e;
  vertical-align: middle;
  color: #f5f5f5;
  font-size: 0.9rem;
}

.spr-table tbody tr:hover {
  background: rgba(207, 167, 87, 0.06);
}

.spr-text-success {
  color: #5dffb0 !important;
  font-weight: 600;
}

.spr-text-danger {
  color: #ff7b8f !important;
  font-weight: 600;
}

.spr-empty {
  color: #b0b0b0;
}

.spr-page-info {
  font-size: 0.85rem;
  font-weight: 600;
  color: #e0e0e0;
}

.spr-page-btn {
  background: transparent;
  border: 1px solid rgba(207, 167, 87, 0.5);
  color: #f0d78c;
  font-weight: 600;
}

.spr-page-btn:disabled {
  opacity: 0.45;
  color: #888;
  border-color: #444;
}
</style>
